// Refactored and improved image upload logic

import JSZip from "jszip";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrUpdateProject, addImages } from "./crudFunctions";

// Utility to format ETA
const formatETA = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Upload API handler

// Upload logic inside a React component
const useImageUpload = ({ projectInput, userName, setProjectName, setNameConfirmed }) => {
  const [uploadProgress, setUploadProgress] = useState({
    totalBatches: 0,
    completedBatches: 0,
    failedBatches: [],
    isUploading: false,
    eta: "",
    speed: 0,
    dummyProgress: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (uploadProgress.isUploading && uploadProgress.totalBatches > 0) {
      interval = setInterval(() => {
        setUploadProgress((prev) => {
          const target = ((prev.completedBatches + 1) / prev.totalBatches) * 100;
          const next = Math.min(prev.dummyProgress + 1, target);
          return next > prev.dummyProgress ? { ...prev, dummyProgress: next } : prev;
        });
      }, 200);
    }
    return () => clearInterval(interval);
  }, [uploadProgress.isUploading, uploadProgress.totalBatches, uploadProgress.completedBatches]);

  useEffect(() => {
    setUploadProgress((prev) => ({
      ...prev,
      dummyProgress: (prev.completedBatches / prev.totalBatches) * 100,
    }));
  }, [uploadProgress.completedBatches]);

  const handleNameAdd = async () => {
    const finalName = projectInput.trim();
    if (!finalName) return alert("Project name is required");
    try {
      await createOrUpdateProject(finalName);
      setProjectName(finalName);
      setNameConfirmed(true);
      alert(`Project ${finalName} created successfully`);
    } catch (error) {
      alert(error.response?.data?.error || "Something Went Wrong");
    }
  };

  const extractImagesFromInput = async (input) => {
    if (input instanceof File && input.name.endsWith(".zip")) {
      const zip = await new JSZip().loadAsync(input);
      const entries = [];
      for (const file of Object.values(zip.files)) {
        if (!file.dir && file.name.match(/\.(jpg|jpeg|png|gif|bmp)$/i)) {
          const blob = await file.async("blob");
          entries.push({ name: file.name, blob });
        }
      }
      return entries;
    } else if (Array.isArray(input) || input instanceof FileList) {
      return Array.from(input)
        .filter((f) => f.name.match(/\.(jpg|jpeg|png|gif|bmp)$/i))
        .map((f) => ({ name: f.name, blob: f }));
    }
    throw new Error("Unsupported input type");
  };

  const handleUpload = async (input) => {
    setIsLoading(true);
    const startTime = Date.now();
    setUploadProgress({ totalBatches: 0, completedBatches: 0, failedBatches: [], isUploading: true, eta: "", speed: 0, dummyProgress: 0 });

    try {
      console.log("Starting upload...");
      const rawImages = await extractImagesFromInput(input);
      if (rawImages.length === 0) throw new Error("No valid images found in the input");
      // console.log("Extracted images:", rawImages);
      const images = rawImages.map((img, idx) => ({ id: idx, name: img.name, blob: img.blob }));
      const totalSizeMB = images.reduce((sum, img) => sum + img.blob.size / 1024 / 1024, 0);
      const MAX_SIZE_MB = 10240;
      if (totalSizeMB > MAX_SIZE_MB) {
        const avg = totalSizeMB / images.length;
        alert(`Upload exceeds 10GB. Avg image: ${avg.toFixed(2)}MB. Try reducing count.`);
        setIsLoading(false);
        return;
      }

      const avgImageSizeMB =
        totalSizeMB / images.length;
      console.log('avg img size', avgImageSizeMB)
      // Decide batch size so each batch is around 100 MB
      const MAX_BATCH_SIZE_MB = 20;
      const batchSize = Math.floor(MAX_BATCH_SIZE_MB / avgImageSizeMB) || 1;
      console.log('batch size calculated', batchSize)
      // Split images into batches
      const batches = [];
      for (let i = 0; i < images.length; i += batchSize) {
        batches.push(images.slice(i, i + batchSize));
      }
      setUploadProgress((prev) => ({ ...prev, totalBatches: batches.length }));
      console.log('Number of batches:', batches.length)
      const failedBatches = [];
      let uploadedCount = 0;

      for (const [index, batch] of batches.entries()) {
        try {
          await addImages(batch, projectInput);
          console.log(`Batch ${index + 1} uploaded successfully`);
          uploadedCount += batch.length;
          const elapsed = (Date.now() - startTime) / 1000;
          const uploadedMB = images.slice(0, uploadedCount).reduce((sum, img) => sum + img.blob.size / 1024 / 1024, 0);
          const speed = uploadedMB / elapsed;
          const eta = (totalSizeMB - uploadedMB) / speed;
          setUploadProgress((prev) => ({
            ...prev,
            completedBatches: prev.completedBatches + 1,
            speed: parseFloat(speed.toFixed(1)),
            eta: formatETA(eta),
          }));
        } catch (err) {
          console.error(`Batch ${index + 1} failed`, err);
          failedBatches.push({ batch, batchNumber: index + 1 });
        }
      }

      setUploadProgress((prev) => ({ ...prev, isUploading: false, failedBatches }));

      if (failedBatches.length === 0) {
        alert("Upload complete!");
        navigate(`/project/${userName}/${projectInput}`);
      } else {
        const retry = window.confirm(`${failedBatches.length} batches failed. Retry?`);
        if (retry) await retryFailedBatches(failedBatches);
        else navigate(`/project/${userName}/${projectInput}`);
      }
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const retryFailedBatches = async (failedBatches) => {
    setIsLoading(true);
    setUploadProgress((prev) => ({ ...prev, isUploading: true, failedBatches: [] }));
    const retryFails = [];

    for (const { batch, batchNumber } of failedBatches) {
      try {
        await addImages(batch, projectInput);
        setUploadProgress((prev) => ({ ...prev, completedBatches: prev.completedBatches + 1 }));
      } catch (err) {
        console.error(`Retry failed for batch ${batchNumber}`);
        retryFails.push({ batch, batchNumber });
      }
    }

    setUploadProgress((prev) => ({ ...prev, isUploading: false, failedBatches: retryFails }));
    if (retryFails.length === 0) {
      alert("All retry batches uploaded!")
      navigate(`/project/${userName}/${projectInput}`)
    }
    else { alert(`${retryFails.length} batches still failed.`) };
    setIsLoading(false);
  };

  return { handleNameAdd, handleUpload, retryFailedBatches, uploadProgress, isLoading };
};

export default useImageUpload;
